import { produce } from 'immer';
import { createReducer, getType } from 'typesafe-actions';

import { TenantInfo, User } from '..';

import { AuthActions } from './index';

import { storage } from '@/utils';

const stateKey = 'auth';

interface AuthState {
    user: User | undefined;
    tenant: TenantInfo | undefined;
    invitationToken: string | null;
}

/* ------------- Initial State ------------- */
const INITIAL_STATE: AuthState = {
    user: undefined,
    tenant: undefined,
    invitationToken: null
};

/* ------------- Reducers ------------- */
const setUser = (state: AuthState, { payload: { user } }: ReturnType<typeof AuthActions.setUser>): AuthState =>
    produce(state, (draft) => {
        draft.user = user;
    });

const setTenant = (state: AuthState, { payload: { tenant } }: ReturnType<typeof AuthActions.setTenant>): AuthState =>
    produce(state, (draft) => {
        draft.tenant = tenant;
    });

const setInvitationToken = (state: AuthState, { payload: { token } }: ReturnType<typeof AuthActions.setInvitationToken>): AuthState =>
    produce(state, (draft) => {
        draft.invitationToken = token;
    });

const logout = (state: AuthState): AuthState =>
    produce(state, (draft) => {
        draft.user = undefined;
        draft.tenant = undefined;
        storage.clearToken();
    });

/* ------------- Hookup Reducers To Types ------------- */
const reducer = createReducer(INITIAL_STATE, {
    [getType(AuthActions.setUser)]: setUser,
    [getType(AuthActions.setTenant)]: setTenant,
    [getType(AuthActions.setInvitationToken)]: setInvitationToken,
    [getType(AuthActions.logout)]: logout
});

const reducerMap = { [stateKey]: reducer };

/* ------------- Selectors ------------- */
// use any for root state for now because of Dependency cycle error
// TODO: resolve dependency cycle error
// eslint-disable-next-line
const getReducerState = (state: any): AuthState => state[stateKey];

const selectors = {
    getUser: (state: AuthState): User | undefined => state.user,
    getTenant: (state: AuthState): TenantInfo | undefined => state.tenant,
    getInvitationToken: (state: AuthState): string | null => state.invitationToken
};

/* ------------- Export ------------- */
export default {
    // default export
    selectors,

    INITIAL_STATE,

    stateKey,
    getReducerState,
    reducer,
    reducerMap
};
